import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import Image from 'next/image';
import posthog from 'posthog-js';
import { utils } from '@makeship/core';
import { useStore } from '../../../../store';
import { Overline, S1, S2, S3 } from '../../../Typography';
import { isUserLoggedIn } from '../../../../utils/analytics';
import { parseFeaturedCollectionMetafields } from '../../../../utils/collections';
import { hasProductTag, parseProductMetafieldsFromCollection } from '../../../../utils/product';
import Countdown from '../../../Countdown';
import { ProductTag } from '../../../../types/common';

const CollectionsDisplayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 14px;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    gap: 22px;
  }
`;

const CollectionCard = styled.div`
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    width: calc(50% - 8px);
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    width: calc(50% - 14px);
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    width: calc(50% - 22px);
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.maxContent}px) {
    width: calc(33.3333% - 24px);
  }
`;

const Hyperlink = styled.a``;

const ImageWrapper = styled.div<{ backgroundColor?: string; hasBackgroundImage?: boolean }>`
  width: 100%;
  object-fit: cover;
  height: 168px;
  position: relative;
  border-radius: 12px;
  background: ${({ theme, hasBackgroundImage, backgroundColor }) =>
    !hasBackgroundImage && (backgroundColor || theme.colors.neutral2)};

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    &:hover {
      .gradient-overlay {
        opacity: 1;
      }
    }
  }
`;

const StyledImage = styled(Image)`
  border-radius: 12px;
`;

const TextWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 4px;
`;

const CreatorTitle = styled(S1)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-grow: 0;
  max-width: 80%;
`;

const ProductsOverlayContainer = styled.div<{ showProducts: boolean }>`
  position: absolute;
  display: flex;
  opacity: 0;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  gap: 4px;
  transition: opacity 0.3s ease;
  opacity: ${({ showProducts }) => (showProducts ? '1' : '0')};

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    opacity: 0;
    &:hover {
      opacity: 1;
    }
  }
`;

const ProductImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  object-fit: cover;
  width: 70px;
  height: 70px;
  border-radius: 12px;
  box-shadow: 0px 2px 8px 0px rgba(20, 20, 20, 0.2);
  background: ${({ theme }) => utils.hexToRGBA(theme.colors.neutral1, 90)};
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    width: 100px;
    height: 100px;
  }
`;

const MoreText = styled.div`
  display: flex;
  flex-direction: column;
`;

const CountdownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PillOverlayContainer = styled.div<{ hasDiscount?: boolean }>`
  position: absolute;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  top: 4px;
  right: 4px;
  background: ${({ theme, hasDiscount }) =>
    hasDiscount ? theme.colors.discountPillBackground : theme.colors.unlockGiftPillBackground};
  padding: 4px 10px;
  border-radius: 45px;
`;

const StyledOverline = styled(Overline)`
  font-weight: 700;
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => `linear-gradient(to top, ${theme.colors.neutral1} 0%, transparent 100%)`};
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 10px;
`;

type CollectionsDisplayProps = {
  creatorCollections: CollectionEdgeResponse[];
  sectionName: string;
};

const CollectionsDisplay: React.FC<CollectionsDisplayProps> = ({
  creatorCollections,
  sectionName,
}: CollectionsDisplayProps) => {
  const { state } = useStore();
  const [showProductsOverlayFor, setShowProductsOverlayFor] = React.useState('');
  const targetRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const threshold = 300;
        let closestElement = null;
        let minDistance = Infinity;

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const targetId = entry.target.getAttribute('data-id');
            if (targetId) {
              const boundingTop = entry.boundingClientRect.top;
              const midpoint = boundingTop + entry.boundingClientRect.height / 2;
              const distance = Math.abs(midpoint - threshold);

              if (distance < minDistance) {
                minDistance = distance;
                closestElement = targetId;
              }
            }
          }
        });

        const newValue = closestElement || '';

        // Only update if the value actually changed
        setShowProductsOverlayFor((prev) => (prev !== newValue ? newValue : prev));
      },
      {
        threshold: 0.4,
        rootMargin: '100px 0px 0px 0px',
      },
    );

    // Observe each target element
    Object.values(targetRefs.current).forEach((target) => {
      if (target) {
        observer.observe(target);
      }
    });

    // Cleanup function
    return () => {
      Object.values(targetRefs.current).forEach((target) => {
        if (target) {
          observer.unobserve(target);
        }
      });
    };
  }, [showProductsOverlayFor]);

  const renderCollectionItems = () =>
    creatorCollections.map((collection, index) => {
      const { node } = collection;
      const { metafields } = node;
      const {
        headerBackgroundURL,
        backgroundColour,
        hasDiscount,
        productDropCollections,
      } = parseFeaturedCollectionMetafields(metafields.edges);
      const products = node.products || [];
      const productMetafields = products?.[0]?.node?.metafields?.edges || [];
      const { enddate } = parseProductMetafieldsFromCollection(productMetafields);
      const isNonStandardCampaign =
        hasProductTag(products[0].node.tags, ProductTag.Evergreen) ||
        hasProductTag(products[0].node.tags, ProductTag.Hybrid) ||
        hasProductTag(products[0].node.tags, ProductTag.Limited);

      return (
        <CollectionCard key={node.handle}>
          <Link href={`/shop/creator/${node.handle}`} passHref>
            <Hyperlink
              onClick={() => {
                posthog.capture('featuredCreator_collectionCard_click', {
                  creator_handle: node.handle,
                  creator_title: node.title,
                  is_logged_in: isUserLoggedIn(state.user),
                  section_name: sectionName,
                  link_href: `/shop/creator/${node.handle}`,
                });
              }}
            >
              <ImageWrapper backgroundColor={backgroundColour} hasBackgroundImage={!!headerBackgroundURL}>
                {headerBackgroundURL && (
                  <>
                    <StyledImage
                      src={headerBackgroundURL}
                      layout="fill"
                      objectFit="cover"
                      alt={`${node.title} collection`}
                      priority
                      quality={90}
                      unoptimized
                    />
                    <GradientOverlay className="gradient-overlay" />
                  </>
                )}
                {(hasDiscount || productDropCollections) && (
                  <PillOverlayContainer hasDiscount={hasDiscount}>
                    <StyledOverline>{hasDiscount ? '💲 Discounts!' : '⭐️ Unlock Gift!'}</StyledOverline>
                  </PillOverlayContainer>
                )}
                <ProductsOverlayContainer
                  showProducts={showProductsOverlayFor === node.handle}
                  // eslint-disable-next-line no-return-assign
                  ref={(el) => {
                    targetRefs.current[node.handle] = el;
                  }}
                  data-id={node.handle}
                  data-index={index}
                >
                  {products.length > 0 &&
                    products.slice(0, 3).map((productEdge: Shopify.ProductEdge) => (
                      <ProductImageWrapper key={productEdge.node.id}>
                        <StyledImage
                          src={productEdge.node.featuredMedia?.image?.transformedSrc || ''}
                          alt={productEdge.node.title}
                          height={100}
                          width={100}
                          quality={90}
                          priority
                          unoptimized
                        />
                      </ProductImageWrapper>
                    ))}
                  {products.length > 3 && (
                    <ProductImageWrapper>
                      <MoreText>
                        <S2>& More!</S2>
                        <S2>➡️</S2>
                      </MoreText>
                    </ProductImageWrapper>
                  )}
                </ProductsOverlayContainer>
              </ImageWrapper>
              <TextWrapper>
                <CreatorTitle>{node.title}</CreatorTitle>
                {!!enddate && !isNonStandardCampaign && (
                  <CountdownWrapper>
                    🕓
                    <S3>
                      <Countdown date={enddate} />
                    </S3>
                  </CountdownWrapper>
                )}
              </TextWrapper>
            </Hyperlink>
          </Link>
        </CollectionCard>
      );
    });

  return <CollectionsDisplayWrapper>{renderCollectionItems()}</CollectionsDisplayWrapper>;
};

export default CollectionsDisplay;
